      

const BoosterNativeBridge = (function() {
    const callbackMap = new Map(); // 存储回调函数，key：回调ID
    let callbackId = 0;
    
    // 存储push消息回调函数
    let pushMessageCallback = null;

    // 存储语言变化监听器（Native 通过 window.onLocaleChanged 主动推送语言）
    let localeChangeListener = null;

    /**
     * 前端调用 Native 方法（带返回值，Promise 版本）
     * @param methodName  方法名
     * @param params  参数
     * @returns  Promise 结果（resolve 成功值，reject 错误信息）
     */
    function callNativeWithResult(methodName, params = {}) {
        return new Promise((resolve, reject) => {
            if (!methodName) {
                reject(new Error("方法名不能为空"));
                return;
            }

            // 生成唯一回调ID，关联 resolve/reject
            const id = 'cb_' + Date.now() + '_' + callbackId++;
            callbackMap.set(id, { resolve, reject });

            // 构造请求数据（包含回调ID，供 Native 回传结果）
            const requestData = {
                method: methodName,
                params: params,
                callbackId: id
            };

            try {
                // 直接按注入的桥对象判断，不依赖 UA（避免自定义 UA / iPad「Macintosh」UA 导致误判）
                const androidBridge = window.NativeBridge && window.NativeBridge.postMessage;
                const iosHandler = window.webkit?.messageHandlers?.nativeWithResult;
                if (androidBridge) {
                    console.log('Android 调用 Native 方法：', methodName, params);
                    window.NativeBridge.postMessage(JSON.stringify(requestData));
                } else if (iosHandler?.postMessage) {
                    iosHandler.postMessage(requestData); // iOS 支持直接传对象
                } else {
                    reject(new Error("非 Native 环境（未检测到 Native 桥）"));
                }
            } catch (e) {
                reject(e);
            }
        });
    }

    /**
     * Native 回传结果时调用的前端方法（Native 会主动调用）
     * @param response  Native 回传的数据（{ callbackId, data, error } ）
     */
    function onNativeResult(response) {
        try {
            const result = typeof response === "string" ? JSON.parse(response) : response;
            const { callbackId, data, error } = result;

            const { resolve, reject } = callbackMap.get(callbackId) || {};

            if (resolve && reject) {
                if (error) {
                    reject(new Error(error.message || "Native 调用失败"));
                } else {
                    resolve(data); // 返回值通过 resolve 传递给前端
                }
                callbackMap.delete(callbackId); // 移除已执行的回调，避免内存泄漏
            }
        } catch (e) {
            console.error("解析 Native 返回值失败：", e);
        }
    }

    /**
     * 注册push消息回调函数
     * @param onMessage 消息接收回调函数
     */
    function registerPushCallback(onMessage) {
        if (typeof onMessage === 'function') {
            pushMessageCallback = onMessage;
            console.log('Push消息回调注册成功');
            return true;
        } else {
            console.warn('Push消息回调注册失败：参数不是函数');
            return false;
        }
    }

    /**
     * 取消注册push消息回调
     */
    function unregisterPushCallback() {
        pushMessageCallback = null;
        console.log('Push回调已取消注册');
        return true;
    }

    /**
     * Native调用此方法发送push消息到Web
     * @param messageData push消息数据
     */
    function onPushMessageFromNative(messageData) {
        try {
            const message = typeof messageData === "string" ? JSON.parse(messageData) : messageData;
            console.log('收到Native push消息:', message);
            
            if (pushMessageCallback) {
                pushMessageCallback(message);
            } else {
                console.warn('收到Native push消息，但未注册消息回调函数');
            }
        } catch (e) {
            console.error('解析Native push消息失败:', e);
        }
    }

    /**
     * Native 在 App 切换语言后主动调用，推送当前语言给 H5
     * @param locale  "zh" | "en"
     */
    function onLocaleChanged(locale) {
        if (locale === "zh" || locale === "en") {
            console.log('收到 Native 语言变化推送:', locale);
            if (localeChangeListener) {
                localeChangeListener(locale);
            }
        } else {
            console.warn('onLocaleChanged 收到非法语言参数:', locale);
        }
    }

    /**
     * 注册语言变化监听器（由 i18n 层调用）
     * @param onChange 收到 "zh" | "en" 时回调
     */
    function setLocaleChangeListener(onChange) {
        if (typeof onChange === 'function') {
            localeChangeListener = onChange;
            return true;
        }
        console.warn('setLocaleChangeListener 参数不是函数');
        return false;
    }

    // 挂载到 window，供 Native 调用
    window.onNativeResult = onNativeResult;
    window.onPushMessageFromNative = onPushMessageFromNative;
    window.onLocaleChanged = onLocaleChanged;

    return {
        // Native调用方法
        close: () => callNativeWithResult("sendMessageToNative", { command: "close" }),

        sendMessageToAgent: (generalParameters) => callNativeWithResult("sendMessageToAgent", generalParameters),
        getParameters: () => callNativeWithResult("sendMessageToAgent", {
            command: "load_param_page",
        }),
        saveParameters: (params) => callNativeWithResult("sendMessageToAgent", {
            command: "update_param",
            parameters: params
        }),
        // 获取当前语言环境："zh" 或 "en"；拿不到/非 Native 环境时返回 null，由调用方兜底
        getLocale: () => callNativeWithResult("sendMessageToNative", { command: "getLocale" })
            .then((res) => {
                    console.log('[jsBridge] getLocale 原始返回:', res);
                // 兼容多种返回：{locale:"zh"} / {data:{locale:"zh"}} / "zh" / {language:"zh"}
                const raw = (res && typeof res === 'object' && 'data' in res) ? res.data : res;
                const candidate = typeof raw === 'string'
                    ? raw
                    : (raw && (raw.locale || raw.language || raw.lang));
                const locale = (candidate === 'en' || candidate === 'zh') ? candidate : null;
                console.log('[jsBridge] getLocale 原始返回:', res, '→ 解析为:', locale);
                return { locale };
            })
            .catch((e) => {
                console.warn('[jsBridge] getLocale 调用失败:', e);
                return { locale: null };
            }),

        // // H5 侧主动切换语言后回传 Native，让 App 跟着切（H5 → App 方向）
        // // 契约：{ method: "sendMessageToAgent", params: { command: "setLocale", locale: "zh" | "en" } }
        // // 保守实现：非 Native 环境或 App 未实现该命令时静默返回 { ok: false }，绝不影响 H5 自身切语言
        // setLocale: (locale) => {
        //     if (locale !== 'zh' && locale !== 'en') {
        //         console.warn('[jsBridge] setLocale 收到非法语言参数:', locale);
        //         return Promise.resolve({ ok: false });
        //     }
        //     const androidBridge = window.NativeBridge && window.NativeBridge.postMessage;
        //     const iosHandler = window.webkit?.messageHandlers?.nativeWithResult;
        //     if (!androidBridge && !iosHandler?.postMessage) {
        //         // 浏览器调试环境：没有桥，直接跳过，不抛错
        //         return Promise.resolve({ ok: false, reason: 'no-native-bridge' });
        //     }
        //     return callNativeWithResult("sendMessageToAgent", { command: "setLocale", locale })
        //         .then(() => ({ ok: true }))
        //         .catch((e) => {
        //             console.warn('[jsBridge] setLocale 回传失败（App 可能未实现该命令）:', e);
        //             return { ok: false };
        //         });
        // },

        // Push回调相关方法
        registerPushCallback: registerPushCallback,
        unregisterPushCallback: unregisterPushCallback,
        isPushCallbackRegistered: () => pushMessageCallback !== null,

        // 语言变化监听（Native 主动推送语言时回调）
        setLocaleChangeListener: setLocaleChangeListener
    };
})();

window.BoosterNativeBridge = BoosterNativeBridge;

    
export default BoosterNativeBridge;


    